import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import verified from '../assets/images/verified.svg';
import commentsend from '../assets/images/comment_send.svg';
import drop_horz from '../assets/images/drop_horz.svg';
import TipButton from './TipButton';
import config from '../config/config';

const PostMetaSingle = ({
  heart_icon,
  heart_icon_fill,
  comment_icon,
  tip_icon,
  bookmark_icon,
  bookmark_icon_fill,
  postId,
  userId,
}) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [mediaType, setMediaType] = useState('');
  const [media, setMedia] = useState([]);
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const [commentLikes, setCommentLikes] = useState({});
  const [userDetails, setUserDetails] = useState(null);
  const [replyToCommentId, setReplyToCommentId] = useState(null)
  const [userName, setUserName] = useState('');
  const [comment, setComment] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [replyToUser, setReplyToUser] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [post, setPost] = useState(null);
  const commonEmojis = ['😊', '😂', '😍', '🥺', '😎', '😢', '😠', '😋','🙌','😁','😉', '👍', '🙏', '🥳'];
  const displayedComments = comments;

  const postUser = {
    id: post?.user?.id,
    name: post?.user?.name,
    user_name: post?.user?.user_name,
    profile: post?.user?.user_details?.profile,
    status: post?.user?.status,
    role: post?.user?.role,
  };


  const fetchPostData = async () => {
    if (!postId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.baseURL}/get-posts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const post = response.data.posts.find(post => post.id === postId);

      if (post) {
        setIsLiked(post.liked_status === 'active');
        setLikesCount(post.total_likes);
        setIsBookmarked(post.bookmark_status === 'active');
        setMediaType(post.media.length ? post.media[0].url : '');
        setComments(post.comments || []);
        setCommentsCount(post.comments_count?.aggregate || 0);
        setPost(post);
        setIsSubscribed(post.is_subscribed);
        // Initialize comment likes status
        const initialCommentLikes = {};
        post.comments.forEach(comment => {
          initialCommentLikes[comment.id] = {
            isLiked: comment.liked_status === 'active',
            likesCount: comment.total_likes || 0,
          };
        });
        setCommentLikes(initialCommentLikes);
      } else {
        console.warn('Post not found');
      }
    } catch (error) {
      console.error('Error fetching post data:', error);
    }
  };

  useEffect(() => {
    fetchPostData();
  }, [postId]);

  const handleToggleLikePost = async () => {
    if (!postId) {
      console.error('Post ID is missing');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${config.baseURL}/create-post-like`;

      const response = await axios.post(
        endpoint,
        { post_id: postId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setIsLiked(prev => {
          const newLikeStatus = !prev;
          setLikesCount(prevCount => newLikeStatus ? prevCount + 1 : prevCount - 1);
          return newLikeStatus;
        });
      } else {
        console.error('Error response from server:', response);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

const handleToggleBookmarkPost = async () => {
  if (!postId) {
    console.error('Post ID is missing');
    return;
  }

  try {
    const token = localStorage.getItem('token');
    const endpoint = `${config.baseURL}/bookmark-post`;

    // Construct the media array as per your requirements
    const mediaArray = media.map((item) => ({
      id: item.id,
      post_id: postId, // Assuming you want to associate this media with the current post
      url: item.url,
      file_type: item.file_type, // Ensure this is set correctly based on your media logic
    }));

    const response = await axios.post(
      endpoint,
      { 
        post_id: postId, 
        media: mediaArray // Send the constructed media array
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status === 200) {
      setIsBookmarked(prev => !prev);
    } else {
      console.error('Error response from server:', response);
    }
  } catch (error) {
    console.error('Error toggling bookmark:', error);
  }
};

  const toggleCommentsVisibility = () => {
    setCommentsVisible(!commentsVisible);
  };

useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/current-user-details`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`, // Adjust token key if necessary
          },
        });
        const userDetailsData = response.data.data.user_details;
        const userNameData = response.data.data.user.user_name;

        setUserDetails(userDetailsData);
        setUserName(userNameData);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, []);


  const handleEmojiClick = (emoji) => {
    setComment(prevText => prevText + emoji);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(prevState => !prevState);
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const formData = new URLSearchParams();
    formData.append('post_id', postId);
    formData.append('comment_text', comment);

    // Append comment_id if replying to a comment
    if (replyToCommentId) {
      formData.append('comment_id', replyToCommentId);
    }

    console.log('Form Data:', formData.toString()); // Log form data for debugging

    const response = await axios.post(`${config.baseURL}/create-post-comment`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    console.log('API Response:', response.data); // Log API response for debugging

    if (response.data.success) {
      setComment('');
      setReplyToUser(null); // Clear reply state
      setReplyToCommentId(null); // Clear the comment ID state
      setShowEmojiPicker(false);
      fetchPostData(); // Refresh the post data to show the new comment
    }
  } catch (error) {
    console.error('Error submitting comment:', error);
  }
};


const handleReplyClick = (userName, commentId) => {
  setReplyToUser(userName); // Set the username for reply context
  setReplyToCommentId(commentId); // Set the comment ID for replying
  setComment(prev => `${prev ? ' ' : ''}@${userName} `); // Optionally include username in comment text
};


  const handleToggleCommentLike = async (commentId) => {
    if (!commentId) {
      console.error('Comment ID is missing');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${config.baseURL}/like-post-comment`;

      const response = await axios.post(
        endpoint,
        new URLSearchParams({ comment_id: commentId }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.status === 200) {
        setCommentLikes(prevLikes => {
          const updatedLikes = { ...prevLikes };
          const isLiked = !prevLikes[commentId].isLiked;
          updatedLikes[commentId] = {
            isLiked,
            likesCount: prevLikes[commentId].likesCount + (isLiked ? 1 : -1),
          };
          return updatedLikes;
        });
      } else {
        console.error('Error response from server:', response);
      }
    } catch (error) {
      console.error('Error toggling comment like:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId) {
      console.error('Comment ID is missing');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${config.baseURL}/post-comment-delete`;

      const response = await axios.post(
        endpoint,
        new URLSearchParams({ comment_id: commentId }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.status === 200) {
        // Refetch post data to update comments after deletion
        fetchPostData();
      } else {
        console.error('Error response from server:', response);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const getInitials = (fullName) => {
    return fullName.split(' ').map(part => part.charAt(0)).join('');
  };


  return (
    <>
      <div className="post_meta_likes">
        <div className="connect_box">
          <div className="post_meta_likes_left">
            <div className={`buttoms_meta ${!isSubscribed ? 'disabled' : ''}`}>
              <Link 
                onClick={handleToggleLikePost}
                className={isSubscribed ? '' : 'disabled-link'}
              >
                <img
                  src={isLiked ? heart_icon_fill : heart_icon}
                  alt="Like"
                />
              </Link>
              <Link 
                className={`comments_block ${isSubscribed ? '' : 'disabled-link'}`}
                onClick={isSubscribed ? toggleCommentsVisibility : undefined}
              >
                <img src={comment_icon} alt="Comment" />
              </Link>
              <div 
                className={`send_tip ${isSubscribed ? '' : 'disabled-link'}`}
              >
              {post && !post.is_auth_user_post && (
                <TipButton 
                  imageSrc={tip_icon} 
                  altText="Send Tip" 
                  text="Send Tip"
                  postId={postId}
                  userId={userId}
                  user={postUser}                  
                />
              )}
              </div>
            </div>

          </div>
          <div className="post_meta_likes_right">
            <Link onClick={handleToggleBookmarkPost}>
              <img
                src={isBookmarked ? bookmark_icon_fill : bookmark_icon}
                alt="Bookmark"
              />
            </Link>
          </div>
        </div>
        <div className="like_comment_count">
          <div className="data_show_likes_comment">
            <span>{likesCount} </span> Likes
          </div>
          <div className="data_show_likes_comment">
            <span>{commentsCount} </span> comments
          </div>
        </div>
      </div>

     {commentsVisible && (
      <div className="comment_section">
        {displayedComments.map(comment => (
          <div key={comment.id} className="comment_list">
            {/* User Profile */}
            <div className="comment_user_profile">
              {comment.user.user_details?.profile ? (
                <div className="imageavailable">
                  <Link to={`/${comment.user.user_name}`}>
                    <img src={comment.user.user_details.profile} alt={comment.user.name} />
                  </Link>
                </div>
              ) : (
                <div className="image_not_available">
                  <Link to={`/${comment.user.user_name}`}> {getInitials(comment.user.name)} </Link>
                </div>
              )}
            </div>
            <div className="comment_text_meta">
              <div className="comment_username">
                <Link className="username_comment_verified" to={`/${comment.user.user_name}`}>
                  {comment.user.name} <img src={verified} alt="Verified" />
                </Link>
                <div className="comment_text"> {/* Convert @username mentions to links */}
                {comment.content.split(' ').map((word, index) =>
                  word.startsWith('@') ? (
                    <Link
                      key={index}
                      to={`/${word.slice(1)}`}
                      className="mention_link"
                    >
                      {word}
                    </Link>
                  ) : (
                    word + ' '
                  )
                )}
                </div>
              </div>
              <div className="comment_meta">
                <div className="comment_time">
                  {comment.created_at_human}
                </div>
                  {commentLikes[comment.id]?.likesCount === 1 && (
                    <div className="comment_like_count">
                      {commentLikes[comment.id]?.likesCount} Like
                    </div>
                  )}
                <div className="reply_link">
                  <Link onClick={() => handleReplyClick(comment.user.user_name, comment.id)}> Reply </Link>
                </div> 
                {comment.is_auth_user_comment && (
                <div className="head_dropdown post_dropdown">
                  <ul>
                    <li className="nav-item dropdown">
                      <a
                        className="nav-link dropdown-toggle"
                        href="#"
                        id="productsDropdown"
                        role="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <img className="comment_dropdown" src={drop_horz} alt="Dropdown icon" />
                      </a>
                      <ul className="dropdown-menu" aria-labelledby="productsDropdown">
                        <li className="dropdown">
                          <Link
                            className="dropdown-item danger_link"
                            onClick={(e) => {
                              e.preventDefault(); // Prevent default link behavior
                              handleDeleteComment(comment.id);
                            }}
                          >
                            Delete
                          </Link>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              )}
              </div>
            </div>
            <div className="comment_like">
              <img
                src={commentLikes[comment.id]?.isLiked ? heart_icon_fill : heart_icon}
                alt="Like"
                onClick={() => handleToggleCommentLike(comment.id)}
              />
            </div>
          </div>
        ))}

        <div className="comment_input_box">
          <div className="comment_emoji">
            {commonEmojis.map(emoji => (
              <button
                key={emoji}
                onClick={() => handleEmojiClick(emoji)}
                className="emoji_button"
              >
                {emoji}
              </button>
            ))}
          </div>
          <div className="comment_input_block">
            <div className="input_block_user">
              {userDetails?.user_profile ? (
                <div className="imageavailable">
                  <a href="#">
                    <img src={userDetails.user_profile} alt="User" />
                  </a>
                </div>
              ) : (
                <div className="image_not_available">
                  {userName.split(' ').map((n) => n[0]).join('')}
                </div>
              )}
            </div>
            <div className="input_block_comment">
              <form onSubmit={handleSubmit}>
                <textarea
                    placeholder="Add a comment..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                <button type="submit" className="comment_submit">
                  <img src={commentsend} alt="Send Comment" />
                </button>
              </form>
            </div>
          </div>
        </div>
        <div className="comment_hide">
          <Link onClick={toggleCommentsVisibility}>
            Hide comments
          </Link>
        </div>
      </div>
    )}
    </>
  );
};

export default PostMetaSingle;
